import { useTheme } from 'vuetify'

/**
 * Упрощенный генератор цветов
 */
export function useChartColors() {
  const theme = useTheme()

  const generateColors = (count: number): string[] => {
    if (count <= 0) return []

    const currentTheme = theme.current.value
    if (!currentTheme?.colors) {
      return generateFallbackColors(count)
    }

    const colors = currentTheme.colors
    const isDark = currentTheme.dark || false

    // Базовый набор цветов из темы
    const themeColors = [
      colors.primary,
      colors.info,
      colors.success,
      colors.warning,
      colors.error,
      colors.secondary,
      colors['primary-darken-1'],
      colors['info-darken-1'],
      colors['success-darken-1'],
      colors['warning-darken-1'],
      colors['error-darken-1'],
    ].filter(Boolean) as string[]

    // Если нужно больше цветов, генерируем дополнительные
    if (count <= themeColors.length) {
      return themeColors.slice(0, count)
    }

    // Дополняем сгенерированными цветами
    const additionalColors = generateHSLColors(count - themeColors.length, isDark)
    return [...themeColors, ...additionalColors]
  }

  const generateColorsWithOpacity = (count: number, opacity: number = 0.8): string[] => {
    const colors = generateColors(count)
    return colors.map(color => {
      if (color.startsWith('hsl')) {
        return color.replace('hsl(', 'hsla(').replace(')', `, ${opacity})`)
      }
      return color
    })
  }

  return {
    generateColors,
    generateColorsWithOpacity,
  }
}

/**
 * Генерация HSL цветов
 */
function generateHSLColors(count: number, isDark: boolean): string[] {
  const colors: string[] = []

  for (let i = 0; i < count; i++) {
    const hue = (i * 360 / count) % 360
    const saturation = isDark ? 70 : 65
    const lightness = isDark ? 40 + (i % 3) * 15 : 50 + (i % 3) * 10

    colors.push(`hsl(${Math.round(hue)}, ${saturation}%, ${lightness}%)`)
  }

  return colors
}

/**
 * Fallback цвета на случай проблем с темой
 */
function generateFallbackColors(count: number): string[] {
  const fallbackColors = [
    '#7367f0',
    '#00cfe8',
    '#28c76f',
    '#ff9f43',
    '#ea5455',
    '#8a8d93',
    '#4caf50',
    '#2196f3',
    '#ffc107',
    '#9c27b0',
  ]

  if (count <= fallbackColors.length) {
    return fallbackColors.slice(0, count)
  }

  // Дополняем сгенерированными цветами
  const additionalColors = generateHSLColors(count - fallbackColors.length, false)
  return [...fallbackColors, ...additionalColors]
}
